// Offline support: IndexedDB read cache, outbox for activity writes, and the hooks the pages share.
// Reads are cached per user as serialized protobuf (base64); the outbox payload never holds the token.
import { useEffect, useMemo } from 'react';
import type { RpcError } from 'grpc-web';
import dayjs from 'dayjs';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { isNetworkError } from '@dowty/core';
import { AuthExpired, createOfflineDb, createOutbox, createReadCache, useOfflineRead } from '@dowty/offline';
import { ActivitatorClient } from './generated/activity_grpc_web_pb';
import { Activities, Activity, ActivityTemplates, Sporttype, Sporttypes } from './generated/activity_pb';
import { ScorerClient } from './generated/score_grpc_web_pb';
import { UserScore, UserScores } from './generated/score_pb';
import type { UserData } from './generated/user_pb';
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS, SSO_ENABLED} from './grpcConfig';
import { tryParseUserDataFromLocalStorage } from './utils/userStorage';

type Message = { serializeBinary(): Uint8Array };
type Callback<R> = (error: RpcError, response: R) => void;

export const encodeMessage = (message: Message) => {
    const bytes = message.serializeBinary();
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += 0x8000)
        binary += String.fromCodePoint(...bytes.subarray(offset, offset + 0x8000));
    return btoa(binary);
};
const decode = (value: string) => Uint8Array.from(atob(value), c => c.codePointAt(0)!);

function unary<R>(invoke: (callback: Callback<R>) => unknown): Promise<R> {
    return new Promise((resolve, reject) => invoke((error, response) => (error || !response ? reject(error ?? new Error('Empty response')) : resolve(response))));
}

export const offlineDb = createOfflineDb('sportdings-offline');
const reads = createReadCache<string, string>(offlineDb, { schemaVersion: 1, maxAgeMs: 7 * 864e5 });

/** Reads one cached protobuf message per user: the cached copy first, then the server's. */
function useRead<M extends Message>(
    name: 'activities' | 'sporttypes' | 'templates' | 'ownScore',
    type: { deserializeBinary(bytes: Uint8Array): M },
    call: (user: UserData, callback: Callback<M>) => unknown,
    reloadOnSave = false,
) {
    const user = tryParseUserDataFromLocalStorage(true);
    const read = useOfflineRead(reads, `${name}:${user?.getUserid()}`, async () => {
        if (!user) throw new Error('No signed-in user');
        return encodeMessage(await unary<M>(callback => call(user, callback)));
    }, [user?.getToken()]);
    const { reload } = read;
    useEffect(() => reloadOnSave ? outbox.subscribe(event => { if (event.type === 'confirmed') reload(); }) : undefined, [reloadOnSave, reload]);
    const data = useMemo(() => (read.data === undefined ? undefined : type.deserializeBinary(decode(read.data))), [read.data, type]);
    return { ...read, data };
}

const activityClient = () => new ActivitatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);

export const useActivities = () => useRead('activities', Activities, (user, callback) => activityClient().getActivities(user, undefined, callback), true);
export const NO_SPORTTYPES = new Sporttypes();
export const useSporttypes = () => useRead('sporttypes', Sporttypes, (user, callback) => activityClient().getSporttype(user, undefined, callback));
export const useTemplates = () => useRead('templates', ActivityTemplates, (user, callback) => activityClient().getActivityTemplates(user, undefined, callback));

/** The user's own scores of the last week; rank-dependent top lists stay online-only. */
export const useOwnScore = () => useRead('ownScore', UserScores, (user, callback) => {
    const request = new UserScores();
    const own = new UserScore();
    own.setUser(user);
    request.setUser(user);
    request.setUsersscoresList([own]);
    request.setDaylength(7);
    return new ScorerClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS).getUserScore(request, undefined, callback);
}, true);

/** A queued write. With an `id` it updates that activity, otherwise it inserts a new one. The sport type travels with it, so sending needs no lookup. */
export type ActivityPayload = { id?: number; ownerId: number; sportype: string; parameterOne: number; parameterTwo: number; tsinsert?: number };

export function toActivity({ id, sportype, parameterOne, parameterTwo, tsinsert }: ActivityPayload): Activity {
    const activity = new Activity();
    if (id !== undefined) activity.setId(id);
    activity.setParameterone(parameterOne);
    activity.setParametertwo(parameterTwo);
    if (tsinsert !== undefined) activity.setTsinsert(Timestamp.fromDate(new Date(tsinsert)));
    activity.setSportype(Sporttype.deserializeBinary(decode(sportype)));
    return activity;
}

async function send(payload: ActivityPayload, idempotencyKey: string): Promise<Activity> {
    // The token is read now, not when the entry was queued.
    const user = tryParseUserDataFromLocalStorage(true);
    if (!user || (!user.getToken() && !SSO_ENABLED) || payload.ownerId !== user.getUserid()) throw new AuthExpired();
    const activity = toActivity(payload);
    activity.setUser(user);
    const metadata = { 'idempotency-key': idempotencyKey };
    try {
        return await unary<Activity>(callback => payload.id === undefined
            ? activityClient().insertActivity(activity, metadata, callback)
            : activityClient().updateActivity(activity, metadata, callback));
    } catch (error) {
        if ((error as RpcError).code === 16) {
            window.dispatchEvent(new Event('sportdings:auth-expired'));
            throw new AuthExpired(); // gRPC UNAUTHENTICATED
        }
        throw error;
    }
}

export const outbox = createOutbox<ActivityPayload, Activity>(offlineDb, {
    send,
    isNetworkError,
    // Entries of another signed-in user stay queued and must not block this user's activities.
    shouldSend: payload => payload.ownerId === tryParseUserDataFromLocalStorage(true)?.getUserid(),
});
export const userOutbox = {
    ...outbox,
    list: async () => {
        const user = tryParseUserDataFromLocalStorage(true);
        return user ? (await outbox.list()).filter(entry => entry.payload.ownerId === user.getUserid()) : [];
    },
};

/** Queues an activity insert (or, with `id`, an update) and tries to send it right away; `date` is DD-MM-YYYY. */
export async function enqueueActivity(sporttypes: Sporttypes, { sport, date, parameterOne, parameterTwo }: { sport: string | null; date?: string | number; parameterOne: number; parameterTwo: number }, id?: number) {
    const sportype = sporttypes.getSportypeList().find(type => type.getSporttypename() === sport);
    if (!sportype) throw new Error('Select a sport');
    const user = tryParseUserDataFromLocalStorage(true);
    if (!user) throw new AuthExpired();
    await outbox.enqueue({ id, ownerId: user.getUserid(), parameterOne, parameterTwo, sportype: encodeMessage(sportype), tsinsert: date ? dayjs(date, 'DD-MM-YYYY').hour(12).valueOf() : undefined });
    void outbox.flush();
}
